import { useToast } from '@components/index'
import { apiClient } from '@services/api-client'
import { useDeleteAttachment } from '@services/contracts/mutations/useDeleteFlatAttachment'
import { useUploadContractAttachment } from '@services/contracts/mutations/useUploadContractAttachment'
import { BACK_OFFICE_PREFIX } from '@services/utils'
import { Download, Eye, FileText, Image as ImageIcon, Paperclip, Trash2, UploadCloud } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { AttachmentOutput, OfferAttachment } from 'types/api'

import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { useConfirm } from '@/components/ui/confirm-dialog'
import { cn } from '@/lib/utils'

interface AttachmentSectionProps {
  attachments:
    | Record<string, AttachmentOutput[]>
    | Record<string, OfferAttachment[]>
    | null
  title: string
  contract_id?: string
  category?: 'AMENDMENT' | 'OTHER'
  type: 'offer' | 'flat'
  /** Hide upload + delete affordances (e.g. for the landlord read-only view). */
  readOnly?: boolean
}

const ACCEPT = 'application/pdf,image/jpeg,image/png'

const isImageFile = (mimeType?: string, fileName?: string): boolean => {
  if (mimeType?.startsWith('image/')) return true
  const ext = fileName?.toLowerCase().split('.').pop()
  return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext || '')
}

const fileExt = (name?: string) =>
  name?.includes('.') ? (name.split('.').pop() || '').toUpperCase() : 'FILE'

export function AttachmentSection({
  attachments,
  title,
  contract_id,
  category,
  type,
  readOnly = false
}: AttachmentSectionProps) {
  const { t } = useTranslation('flat')
  const toast = useToast()
  const { confirm, dialog } = useConfirm()
  const inputRef = useRef<HTMLInputElement>(null)
  const [imageUrls, setImageUrls] = useState<Record<string, string>>({})
  const [dragging, setDragging] = useState(false)

  const { mutateAsync: uploadAttachment } = useUploadContractAttachment()
  const { mutate: deleteAttachment } = useDeleteAttachment()

  const attachUrl = (id: string) =>
    `${BACK_OFFICE_PREFIX}contracts/${contract_id}/attachments/${id}?type=${type}`

  useEffect(() => {
    if (!attachments || !contract_id) return
    const all = Object.values(attachments).flat()
    all
      .filter((a: any) => isImageFile(a.mime_type, a.name))
      .forEach((a: any) => {
        if (imageUrls[a.id]) return
        apiClient.get(attachUrl(a.id), { responseType: 'blob' }).then((res) => {
          const url = URL.createObjectURL(
            new Blob([res.data], { type: res.data.type })
          )
          setImageUrls((prev) => ({ ...prev, [a.id]: url }))
        })
      })
    return () => {
      Object.values(imageUrls).forEach((url) => URL.revokeObjectURL(url))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachments, contract_id, type])

  const openAttachment = (
    id: string,
    action: 'download' | 'view',
    name: string
  ) => {
    apiClient.get(attachUrl(id), { responseType: 'blob' }).then((res) => {
      const url = URL.createObjectURL(
        new Blob([res.data], { type: res.data.type })
      )
      if (action === 'download') {
        const a = document.createElement('a')
        a.href = url
        a.download = name
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
      } else {
        window.open(url, '_blank')
      }
      URL.revokeObjectURL(url)
    })
  }

  const uploadFiles = async (files: FileList | File[]) => {
    for (const file of Array.from(files)) {
      try {
        await uploadAttachment({
          id: contract_id,
          body: { category: category || 'AMENDMENT', files: [file] },
          params: { type }
        })
      } catch (e: any) {
        toast.error({ message: t('uploadFailed', { ns: 'toast', error: e?.message ?? '' }) })
      }
    }
  }

  const removeAttachment = async (id: string) => {
    if (!contract_id) return
    if (
      await confirm({
        title: t('contract.delete_confirm'),
        destructive: true,
        confirmText: t('contract.delete'),
        cancelText: t('contract.no')
      })
    ) {
      deleteAttachment({ contract_id, attachment_id: id, params: { type } })
    }
  }

  const items = attachments ? Object.values(attachments).flat() : []

  const stop =
    (fn: () => void) => (e: React.MouseEvent) => {
      e.stopPropagation()
      fn()
    }

  return (
    <Card className="p-5">
      {dialog}
      <h3 className="m-0 mb-6 flex items-center gap-2.5 font-display text-base font-bold tracking-tight text-ink">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <Paperclip className="h-5 w-5" />
        </span>
        {title}
      </h3>

      {!readOnly && (
        <>
          <input
            ref={inputRef}
            type="file"
            multiple
            accept={ACCEPT}
            className="hidden"
            onChange={(e) => {
              if (e.target.files?.length) uploadFiles(e.target.files)
              e.target.value = ''
            }}
          />
          <div
            role="button"
            tabIndex={0}
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault()
              setDragging(true)
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => {
              e.preventDefault()
              setDragging(false)
              if (e.dataTransfer.files?.length) uploadFiles(e.dataTransfer.files)
            }}
            className={cn(
              'flex cursor-pointer items-center justify-center gap-3 rounded-2xl border-2 border-dashed px-4 py-6 text-center transition-colors',
              dragging
                ? 'border-primary bg-primary/5'
                : 'border-ink/15 hover:border-primary/50 hover:bg-ink/[0.015]'
            )}
          >
            <UploadCloud className="h-6 w-6 shrink-0 text-primary" />
            <span className="text-sm">
              <span className="font-medium text-ink">
                {t('contract.drag_or_click_upload')}
              </span>
              <span className="ml-2 text-xs text-muted-foreground">
                PDF, JPG, PNG
              </span>
            </span>
          </div>
        </>
      )}

      {readOnly && items.length === 0 && (
        <div className="rounded-2xl border border-dashed border-ink/10 px-4 py-8 text-center text-sm text-muted-foreground">
          —
        </div>
      )}

      {items.length > 0 && (
        <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
          {items.map((a: any) => {
            const isImage = isImageFile(a.mime_type, a.name)
            const url = imageUrls[a.id]
            return (
              <div
                key={a.id}
                className="group flex flex-col overflow-hidden rounded-2xl border border-ink/[0.08] bg-white transition-shadow hover:shadow-[0_10px_30px_rgba(4,33,37,0.08)]"
              >
                <div className="relative flex h-28 items-center justify-center overflow-hidden bg-ink/[0.03]">
                  {isImage ? (
                    url ? (
                      <img
                        src={url}
                        alt={a.name}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="h-full w-full animate-pulse bg-ink/[0.06]" />
                    )
                  ) : (
                    <FileText className="h-9 w-9 text-muted-foreground" />
                  )}

                  <div className="absolute inset-0 flex items-center justify-center gap-1.5 bg-ink/50 opacity-0 transition-opacity group-hover:opacity-100 max-sm:opacity-100">
                    <Button
                      type="button"
                      size="icon"
                      aria-label={t('contract.view')}
                      onClick={stop(() => openAttachment(a.id, 'view', a.name))}
                      className="h-9 w-9 rounded-full bg-white text-ink hover:bg-white hover:text-primary"
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button
                      type="button"
                      size="icon"
                      aria-label={t('contract.download')}
                      onClick={stop(() =>
                        openAttachment(a.id, 'download', a.name)
                      )}
                      className="h-9 w-9 rounded-full bg-white text-ink hover:bg-white hover:text-primary"
                    >
                      <Download className="h-4 w-4" />
                    </Button>
                    {!readOnly && (
                      <Button
                        type="button"
                        size="icon"
                        aria-label={t('contract.delete')}
                        onClick={stop(() => removeAttachment(a.id))}
                        className="h-9 w-9 rounded-full bg-white text-destructive hover:bg-white hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-1.5 px-2.5 py-2">
                  <span className="flex shrink-0 items-center gap-1 rounded-md bg-ink/[0.06] px-1.5 py-0.5 text-[10px] font-bold text-muted-foreground">
                    {isImage ? (
                      <ImageIcon className="h-3 w-3" />
                    ) : (
                      <FileText className="h-3 w-3" />
                    )}
                    {fileExt(a.name)}
                  </span>
                  <span className="min-w-0 truncate text-xs text-ink" title={a.name}>
                    {a.name}
                  </span>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </Card>
  )
}
